import React, {
  cloneElement, createContext, useCallback, useContext, useMemo, useState,
} from 'react';
import classNames from 'classnames';
import { Tooltip } from 'antd';
import type { TooltipProps } from 'antd';

const ActivationContext = createContext(true);

export const LazyTooltipScope = ActivationContext.Provider;

export function useLazyTooltipScope() {
  const [activated, setActivated] = useState(false);
  const activate = useCallback(() => setActivated(true), []);
  return useMemo(() => ({ activated, activate }), [activated, activate]);
}

type BoundaryProps = {
  children: React.ReactElement<React.HTMLAttributes<HTMLElement>>;
};

export const LazyTooltipBoundary = function LazyTooltipBoundary(props: BoundaryProps) {
  const { children } = props;
  const { activated, activate } = useLazyTooltipScope();
  const { onMouseEnter } = children.props;

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    onMouseEnter?.(event);
    activate();
  };

  return (
    <LazyTooltipScope value={activated}>
      {cloneElement(children, { onMouseEnter: handleMouseEnter })}
    </LazyTooltipScope>
  );
};

type LazyTooltipProps = Omit<TooltipProps, 'children'> & {
  children: React.ReactElement<React.HTMLAttributes<HTMLElement>>;
};

const LazyTooltip = function LazyTooltip(props: LazyTooltipProps) {
  const { children, ...tooltipProps } = props;
  const activated = useContext(ActivationContext);

  if (activated) {
    return <Tooltip {...tooltipProps}>{children}</Tooltip>;
  }

  const { className } = tooltipProps;
  return cloneElement(children, {
    className: className
      ? classNames(children.props.className, className)
      : children.props.className,
  });
};

export default LazyTooltip;
